import { useEffect, useState } from "react";
import axios from "axios";
import { Landmark } from "lucide-react";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => (n == null ? ", " : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${Math.round(n / 1e3)}K`);
const pct = (n) => (n == null ? ", " : `${n}%`);
const normName = (n) => (n || "").toUpperCase().replace(/[^A-Z0-9 ]/g, " ").replace(/\b(INC|LLC|LLP|LP|CORP|CORPORATION|CO|COMPANY|LTD|THE|PAC|PLC|GROUP|HOLDINGS)\b/g, " ").replace(/\s+/g, " ").trim();
const pacKey = (x) => x.id || normName(x.name);
const sharedOf = (la, lb, keyFn) => {
  const idx = new Map((lb || []).map((x) => [keyFn(x), x]));
  return (la || []).filter((x) => idx.has(keyFn(x))).map((x) => ({ key: keyFn(x), name: x.name, a: x.total, b: idx.get(keyFn(x)).total }))
    .sort((x, y) => (y.a + y.b) - (x.a + x.b));
};
const shortName = (n) => (n || "").replace(/\s*\([^)]*$/, "").replace(/\s*\(.*?\)\s*/g, " ").replace(/political action committee.*$/i, "PAC").replace(/\s+/g, " ").trim();

const MIX = [
  { key: "pacSharePct", label: "PACs", color: "#F7670F" },
  { key: "individualSharePct", label: "Individuals", color: "#38BDF8" },
  { key: "transfersSharePct", label: "Transfers", color: "#A78BFA" },
];
const ROWS = [
  { key: "receipts", label: "Total Raised", fmt: money },
  { key: "cashOnHand", label: "Cash On Hand", fmt: money },
  { key: "pacSharePct", label: "PAC Share", fmt: pct },
  { key: "individualSharePct", label: "Individual Share", fmt: pct },
  { key: "transfersSharePct", label: "Transfers Share", fmt: pct },
  { key: "smallDollarSharePct", label: "Small-Dollar Share (<$200)", fmt: pct },
  { key: "inStateSharePct", label: "In-State Share", fmt: pct },
];

const useFinance = (id) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    let timer = null;
    setD(null);
    if (!id) return undefined;
    const poll = (attempt) =>
      axios.get(`${API}/legislators/${id}/finance`, { validateStatus: (s) => s === 200 || s === 202 })
        .then((r) => {
          if (!alive) return;
          if (r.status === 202) { if (attempt < 8) timer = setTimeout(() => poll(attempt + 1), 8000); else setD({ available: false }); return; }
          setD(r.data);
        })
        .catch((e) => alive && setD({ available: false, locked: e?.response?.status === 403 }));
    poll(0);
    return () => { alive = false; clearTimeout(timer); };
  }, [id]);
  return d;
};

const MixBar = ({ fin, testId }) => {
  const other = Math.max(0, 100 - MIX.reduce((a, m) => a + (fin?.[m.key] || 0), 0));
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-200" data-testid={testId}>
      {MIX.map((m) => (fin?.[m.key] > 0 ? <div key={m.key} style={{ width: `${fin[m.key]}%`, background: m.color }} title={`${m.label} ${fin[m.key]}%`} /> : null))}
      {other > 0 && <div style={{ width: `${other}%`, background: "rgba(120,120,130,0.35)" }} title={`Other ${other}%`} />}
    </div>
  );
};

const BothTag = () => (
  <span className="shrink-0 rounded px-1 py-[1px] text-[9px] font-mono font-bold uppercase tracking-wider bg-orange-500/15 text-orange-600 border border-orange-500/40" data-testid="both-tag">Both</span>
);

const NameCol = ({ items, sharedKeys, keyFn }) => (
  items?.length ? (
    <ul className="space-y-1">
      {items.slice(0, 5).map((x, i) => (
        <li key={i} className="flex justify-between gap-2 text-xs">
          <span className="flex items-center gap-1.5 min-w-0"><span className="text-gray-700 truncate" title={x.name}>{shortName(x.name)}</span>{sharedKeys?.has(keyFn(x)) && <BothTag />}</span>
          <span className="font-mono tabular-nums text-gray-500 shrink-0">{money(x.total)}</span>
        </li>
      ))}
    </ul>
  ) : <p className="text-xs text-gray-400">None itemized this cycle.</p>
);

export const MoneyCompare = ({ legislators }) => {
  const [a, b] = legislators;
  const fa = useFinance(a?.id);
  const fb = useFinance(b?.id);
  if (!a || !b) return null;
  const loading = fa === null || fb === null;
  const has = (f) => Boolean(f?.receipts);
  const short = (leg) => (leg.name || "").split(",")[0];
  const cell = (val, other, fmt, testId) => {
    if (val == null) return <td className="p-2.5 text-right font-mono text-gray-400" data-testid={testId}>n/a</td>;
    const lead = other != null && val > other;
    return <td className={`p-2.5 text-right font-mono tabular-nums ${lead ? "font-bold text-gray-900 bg-orange-50" : "text-gray-700"}`} data-testid={testId}>{fmt(val)}</td>;
  };
  const coverage = [fa?.coverageEnd, fb?.coverageEnd].filter(Boolean).sort().pop();
  const sharedPacs = sharedOf(fa?.topPacs, fb?.topPacs, pacKey);
  const sharedEmployers = sharedOf(fa?.topEmployers, fb?.topEmployers, (x) => normName(x.name));
  const pacKeys = new Set(sharedPacs.map((x) => x.key));
  const empKeys = new Set(sharedEmployers.map((x) => x.key));
  return (
    <Card className="p-6 mb-8 bg-white rounded-xl border-2 border-gray-200" data-testid="money-compare-card">
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-1">
        <Landmark className="w-5 h-5 text-orange-500" /> Campaign Money
      </h2>
      <p className="text-xs text-gray-500 mb-4">Current-cycle FEC filings for each member's principal campaign committee. The larger of the two values is highlighted.</p>
      {loading ? (
        <p className="text-sm text-gray-500 animate-pulse" data-testid="money-compare-loading">Pulling FEC filings…</p>
      ) : fa?.locked || fb?.locked ? (
        <p className="text-sm text-gray-500" data-testid="money-compare-locked">Campaign finance comparison is included in WHIP Pro.</p>
      ) : !has(fa) && !has(fb) ? (
        <p className="text-sm text-gray-500" data-testid="money-compare-unavailable">No FEC filings matched either member for the current cycle.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            {[[a, fa, "a"], [b, fb, "b"]].map(([leg, fin, side]) => (
              <div key={side} data-testid={`money-compare-head-${side}`}>
                <p className="font-semibold text-gray-900 text-sm">{short(leg)}</p>
                <p className="text-[11px] text-gray-500 mb-2 truncate">{has(fin) ? `${fin.cycle} cycle · ${fin.committee || "Principal committee"}` : "No FEC match this cycle"}</p>
                {has(fin) && <MixBar fin={fin} testId={`money-compare-mix-${side}`} />}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-gray-600">
            {MIX.map((m) => <span key={m.key} className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: m.color }} />{m.label}</span>)}
          </div>
          <table className="w-full text-sm" data-testid="money-compare-table">
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.key} className="border-b border-gray-100" data-testid={`money-compare-row-${r.key}`}>
                  <td className="p-2.5 text-gray-700">{r.label}</td>
                  {cell(has(fa) ? fa[r.key] : null, has(fb) ? fb[r.key] : null, r.fmt, `money-compare-${r.key}-a`)}
                  {cell(has(fb) ? fb[r.key] : null, has(fa) ? fa[r.key] : null, r.fmt, `money-compare-${r.key}-b`)}
                </tr>
              ))}
              <tr className="border-b border-gray-100" data-testid="money-compare-row-topDonorStates">
                <td className="p-2.5 text-gray-700">Top Donor States</td>
                <td className="p-2.5 text-right font-mono text-xs text-gray-700">{fa?.topDonorStates?.join(" · ") || "n/a"}</td>
                <td className="p-2.5 text-right font-mono text-xs text-gray-700">{fb?.topDonorStates?.join(" · ") || "n/a"}</td>
              </tr>
            </tbody>
          </table>
          <div className="grid grid-cols-2 gap-4 mt-4 pt-3 border-t border-gray-200">
            <p className="col-span-2 text-[10px] font-mono uppercase tracking-wider text-gray-400">Top PACs</p>
            <div data-testid="money-compare-pacs-a"><NameCol items={fa?.topPacs} sharedKeys={pacKeys} keyFn={pacKey} /></div>
            <div data-testid="money-compare-pacs-b"><NameCol items={fb?.topPacs} sharedKeys={pacKeys} keyFn={pacKey} /></div>
            <p className="col-span-2 text-[10px] font-mono uppercase tracking-wider text-gray-400 mt-2">Top Donor Employers</p>
            <div data-testid="money-compare-employers-a"><NameCol items={fa?.topEmployers} sharedKeys={empKeys} keyFn={(x) => normName(x.name)} /></div>
            <div data-testid="money-compare-employers-b"><NameCol items={fb?.topEmployers} sharedKeys={empKeys} keyFn={(x) => normName(x.name)} /></div>
          </div>
          {has(fa) && has(fb) && (
            <div className="mt-4 pt-3 border-t border-gray-200" data-testid="shared-donors">
              <p className="text-[10px] font-mono uppercase tracking-wider text-orange-500 mb-2" data-testid="shared-donors-count">
                Shared Donors · {sharedPacs.length} PAC{sharedPacs.length === 1 ? "" : "s"} · {sharedEmployers.length} employer{sharedEmployers.length === 1 ? "" : "s"}
                {sharedPacs.length + sharedEmployers.length > 12 ? " · showing the 12 largest" : ""}
              </p>
              {sharedPacs.length + sharedEmployers.length === 0 ? (
                <p className="text-xs text-gray-500" data-testid="shared-donors-none">No PAC or employer among the itemized contributors gave to both members this cycle.</p>
              ) : (
                <table className="w-full text-xs" data-testid="shared-donors-table">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="text-left py-1 font-medium">Donor</th>
                      <th className="text-right py-1 font-medium">{short(a)}</th>
                      <th className="text-right py-1 font-medium">{short(b)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...sharedPacs.map((x) => ({ ...x, kind: "PAC" })), ...sharedEmployers.map((x) => ({ ...x, kind: "Employer" }))].slice(0, 12).map((x, i) => (
                      <tr key={i} className="border-t border-gray-100" data-testid={`shared-donor-${i}`}>
                        <td className="py-1.5 text-gray-700 flex items-center gap-1.5"><span className="truncate" title={x.name}>{shortName(x.name)}</span><span className="text-[9px] font-mono uppercase text-gray-400">{x.kind}</span></td>
                        <td className="py-1.5 text-right font-mono tabular-nums text-gray-700">{money(x.a)}</td>
                        <td className="py-1.5 text-right font-mono tabular-nums text-gray-700">{money(x.b)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-4" data-testid="money-compare-source">
            Source: Federal Election Commission{coverage ? `, filings through ${new Date(coverage).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}` : ""}. Small-dollar and in-state shares are of individual contributions.
          </p>
        </>
      )}
    </Card>
  );
};
